<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { aiTranslate } from '@/lib/aiTranslate.js'
import { reportError } from '@/composables/useSave.js'
import { useUi } from '@/stores/ui.js'

/**
 * "Translate with AI" by a field in another language (D-355): the base language's words
 * translated by the company's AI and put into the field — seen there, saved with the page.
 */
const props = defineProps({
  // The base language's words: a text, or a formatted text.
  source: { type: [String, Object], default: null },
  to: { type: String, required: true },
  from: { type: String, default: null },
  // Whether the field has words already: they are replaced only when the person agrees.
  filled: Boolean,
  // What it translates, when a page has more than one ("title", "text").
  label: { type: String, default: '' },
})
const emit = defineEmits(['translated'])
const { t } = useI18n()
const ui = useUi()
const busy = ref(false)

async function go() {
  if (busy.value || !props.source) return
  if (props.filled && !(await ui.ask({ title: t('aiTranslate.replaceTitle'), message: t('aiTranslate.replace') }))) return
  busy.value = true
  try {
    emit('translated', await aiTranslate(props.source, props.to, props.from))
  } catch (e) {
    if (e?.message === 'timeout') ui.warn(t('aiTranslate.timeout'))
    else reportError(e)
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <button type="button" class="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-brand-700 hover:bg-brand-50 disabled:opacity-50 dark:text-brand-300 dark:hover:bg-brand-900/30"
    :disabled="busy || !source" :title="$t('aiTranslate.hint', { from: (from || 'pl').toUpperCase(), to: to.toUpperCase() })" data-testid="ai-translate" @click="go">
    <i :class="['fa-solid', busy ? 'fa-spinner fa-spin' : 'fa-language']" aria-hidden="true"></i>{{ busy ? $t('aiTranslate.busy') : label || $t('aiTranslate.button') }}
  </button>
</template>
